import moment from "moment";
import React, { useState } from "react";
import { DateRangePicker, isInclusivelyBeforeDay } from "react-dates";
import styled from "styled-components";

import { weekDayMap } from "../../utils/weekConfig";

const ToText = styled.div`
  padding-top: 10px;
  font-size: ${({ theme }) => theme.dropdown.fontSize};
  color: ${({ theme }) => theme.font.pageText.colorMain};
`;

const DateRangePickerButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${({ isApply, theme }) =>
    isApply ? theme.button.primary.bgColor : theme.button.secondary.bgColor};
  border: 1px solid
    ${({ isApply, theme }) => !isApply && theme.button.secondary.borderColor};
  color: ${({ isApply, theme }) =>
    isApply
      ? theme.button.primary.textColor
      : theme.button.secondary.textColor};
  height: 36px;
  padding: 0px 10px;
  cursor: pointer;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  border-radius: ${({ theme }) => theme.button.borderRadius};
  margin: ${({ margin }) => margin || "0"};
  &:hover {
    background-color: ${({ isApply, theme }) =>
      isApply
        ? theme.button.primary.hoverBgColor
        : theme.button.secondary.hoverBgColor};
    color: ${({ theme }) => theme.button.secondary.hoverTextColor};
  }
  @media screen and (max-width: 500px) {
    font-size: 8px;
  }
`;

const CustomInputIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: "-2px" }}>
    <path d="M6.66675 1.66667V4.16667" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.3333 1.66667V4.16667" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2.91675 7.575H17.0834" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17.5 7.08334V14.1667C17.5 16.6667 16.25 18.3333 13.3333 18.3333H6.66667C3.75 18.3333 2.5 16.6667 2.5 14.1667V7.08334C2.5 4.58334 3.75 2.91667 6.66667 2.91667H13.3333C16.25 2.91667 17.5 4.58334 17.5 7.08334Z" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.0788 11.4167H13.0863" stroke="#353A46" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.0788 13.9167H13.0863" stroke="#353A46" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.99632 11.4167H10.0038" stroke="#353A46" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.99632 13.9167H10.0038" stroke="#353A46" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.91185 11.4167H6.91933" stroke="#353A46" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.91185 13.9167H6.91933" stroke="#353A46" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowIcon = () => <ToText>to</ToText>;

const ReactDateRangePicker = ({
  startDate,
  startDateId,
  endDate,
  endDateId,
  onDatesChange,
  disableFutureDates,
  disableCurrentAndFutureDates,
  disablePastDates,
  dateDisplayFormat = localStorage.getItem("date_format") || "DD/MM/YYYY",
  startDayOfWeek = localStorage.getItem("start_day_of_week" || "monday"),
  showFooter = true,
  rangeRestricted,
  disabled,
  onDateSelect,
  selectLastWeek,
  selectLastMonth,
}) => {
  const [focusedInput, setFocusedInput] = useState(null);

  const onApply = () => {
    setFocusedInput(null);
    if (onDateSelect) onDateSelect();
  };

  const handleSelectLastWeek = () => {
    setFocusedInput(null);
    if (selectLastWeek) selectLastWeek();
  };

  const handleSelectLastMonth = () => {
    setFocusedInput(null);
    if (selectLastMonth) selectLastMonth();
  };
  const getCalendarInfo = (showFooter = true) => {
    return (
      <div className="date-range-footer">
        {showFooter && (
          <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <DateRangePickerButton onClick={handleSelectLastWeek}>
                Last 7 days
              </DateRangePickerButton>
              <DateRangePickerButton
                onClick={handleSelectLastMonth}
                margin="0 0 0 8px"
              >
                Last 30 days
              </DateRangePickerButton>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <DateRangePickerButton isApply onClick={onApply}>
                Apply
              </DateRangePickerButton>
            </div>
          </>
        )}
      </div>
    );
  };

  const isOutsideRange = day => {
    if (rangeRestricted) {
      const isBeforeToday = !isInclusivelyBeforeDay(
        day,
        moment().subtract(1, "day"),
      );
      const isAfterToday = isInclusivelyBeforeDay(
        day,
        moment().subtract(1, "day"),
      );
      const startDateMoment = moment(startDate);
      const maxEndDate = startDateMoment.clone().add(3, "months");
      const isBeyondMaxRange = endDate
        ? !isInclusivelyBeforeDay(day, maxEndDate)
        : false;
      if (disableCurrentAndFutureDates) {
        return isBeforeToday || isBeyondMaxRange;
      } else if (disablePastDates) {
        return isAfterToday || isBeyondMaxRange;
      } else {
        return isBeyondMaxRange;
      }
    } else {
      return disableCurrentAndFutureDates
        ? !isInclusivelyBeforeDay(day, moment().subtract(1, "day"))
        : disablePastDates
        ? isInclusivelyBeforeDay(day, moment().subtract(1, "day"))
        : disableFutureDates
        ? !isInclusivelyBeforeDay(day, moment())
        : false;
    }
  };

  return (
    <DateRangePicker
      startDate={startDate}
      startDateId={startDateId}
      endDate={endDate}
      endDateId={endDateId}
      onDatesChange={({ startDate, endDate }) =>
        onDatesChange({ startDate, endDate })
      }
      focusedInput={focusedInput}
      onFocusChange={setFocusedInput}
      showDefaultInputIcon={false}
      customInputIcon={<CustomInputIcon />}
      inputIconPosition="after"
      isOutsideRange={isOutsideRange}
      displayFormat={dateDisplayFormat}
      firstDayOfWeek={weekDayMap[startDayOfWeek]}
      renderCalendarInfo={() => getCalendarInfo(showFooter)}
      calendarInfoPosition="bottom"
      keepOpenOnDateSelect={showFooter}
      customArrowIcon={<ArrowIcon />}
      minimumNights={0}
      hideKeyboardShortcutsPanel
      readOnly
      disabled={disabled}
      numberOfMonths={window.innerWidth < 768 ? 1 : 2}
      // orientation={window.innerWidth < 768 ? "vertical" : "horizontal"}
    />
  );
};

export default ReactDateRangePicker;
